/**
 * TileGrid — the catalogue, as cards or as rows.
 *
 * decision-2026-08-02-tile-is-a-pointer-manage-is-the-workbench: this component
 * carries the two props the retired `isAdminCatalog` boolean used to conflate,
 * and it keeps them apart all the way down to both renderings.
 *
 *   plane      — a property of the DEPLOYMENT (shell/routes planeForMode).
 *   canManage  — a property of the CALLER × THIS TILE. It is a FUNCTION, not a
 *                flag, because tenant-set membership is answered per app: on a
 *                shared vertical broker one tenant-admin sees Manage on their
 *                own tenant's apps and on no others.
 */

import type { TileConfig } from '../config/schema';
import type { Route } from '../shell/Router';
import type { SurfacePlane } from '../shell/routes';
import Tile from './Tile';
import type { CartItem } from '../pages/CartPage';
import TileSkeleton from './TileSkeleton';
import CatalogRow from './CatalogRow';
import { lifecycleBadge } from './catalogBadge';
import type { CatalogView } from '../hooks/useCatalogPrefs';

interface TileGridProps {
  tiles: TileConfig[];
  useBindingFlow?: boolean;
  onAddToCart?: (item: CartItem) => void;
  /** Card shape. Defaults to the tenant broker shape — never leaks an affordance. */
  plane?: SurfacePlane;
  /** auth/surfaceAccess canManageApp(caller, tile.tenant), applied per tile. */
  canManage?: (tile: TileConfig) => boolean;
  /**
   * wp-order-surface-vertical-broker: auth/surfaceAccess canOrderForTenant(caller,
   * tile.tenant), applied per tile — a FUNCTION, not a flag, for the same reason as
   * canManage: tenant-set membership is answered per app. Governs the vertical
   * broker's Order affordance on the management plane. Card view only.
   */
  canOrder?: (tile: TileConfig) => boolean;
  /**
   * decision-2026-08-02-ordering-is-a-tenant-admin-verb — pass-through, see
   * MarketplacePage. Defaults to false: a caller that does not ask for commerce
   * does not get it.
   */
  showCommerce?: boolean;
  onNavigate?: (path: Route) => void;
  /** brief-26: 'list' is management-plane only; tenant brokers always get cards. */
  view?: CatalogView;
  pinnedIds?: string[];
  onTogglePin?: (id: string) => void;
}

interface TileGridSkeletonProps {
  count?: number;
}

export function TileGridSkeleton({ count = 6 }: TileGridSkeletonProps) {
  return (
    <div className="dp-grid">
      {Array.from({ length: count }, (_, i) => <TileSkeleton key={i} />)}
    </div>
  );
}

export default function TileGrid({
  tiles, useBindingFlow = false, onAddToCart, plane = 'consumption', canManage,
  canOrder, showCommerce = false, onNavigate, view = 'tiles', pinnedIds = [], onTogglePin,
}: TileGridProps) {
  const isPinned = (id: string) => pinnedIds.includes(id);
  const management = plane === 'management';
  const mayManage = (tile: TileConfig) => (canManage ? canManage(tile) : false);
  const mayOrder = (tile: TileConfig) => (canOrder ? canOrder(tile) : false);

  if (view === 'list' && management) {
    return (
      <ul role="list" className="dp-list">
        {tiles.map((tile) => {
          const badge = lifecycleBadge(tile);
          return (
            <CatalogRow
              key={tile.id}
              tile={tile}
              badgeClass={badge.cls}
              badgeLabel={badge.label}
              pinned={isPinned(tile.id)}
              onTogglePin={() => onTogglePin?.(tile.id)}
              canManage={mayManage(tile)}
              onNavigate={onNavigate}
            />
          );
        })}
      </ul>
    );
  }

  return (
    <div className="dp-grid">
      {tiles.map((tile) => (
        <Tile
          key={tile.id}
          tile={tile}
          useBindingFlow={useBindingFlow}
          onAddToCart={onAddToCart}
          plane={plane}
          canManage={mayManage(tile)}
          canOrder={mayOrder(tile)}
          showCommerce={showCommerce}
          onNavigate={onNavigate}
          pinned={isPinned(tile.id)}
          onTogglePin={management && onTogglePin ? () => onTogglePin(tile.id) : undefined}
        />
      ))}
    </div>
  );
}
